import React from 'react';
import {
  LayoutDashboard,
  LogOut,
  Heart,
  MapPin,
  MessageCircle,
  Settings,
  UserRound,
  ClipboardPlus,
  ChartNoAxesCombined,
  TrendingUp,
} from 'lucide-react';
import { useTranslations } from '../translations';
import StorefrontIcon from './StorefrontIcon';
import './AccountDropdown.css';

export type AccountDropdownItem =
  | 'profile'
  | 'announcements'
  | 'favorites'
  | 'messages'
  | 'orders'
  | 'orderStatistics'
  | 'orderReport'
  | 'locations'
  | 'administration'
  | 'settings';

interface AccountDropdownProps {
  activeItem?: AccountDropdownItem;
  hasUnreadMessages?: boolean;
  isAdmin?: boolean;
  onProfileClick?: () => void;
  onFavoritesClick?: () => void;
  onLocationsClick?: () => void;
  onMessagesClick?: () => void;
  onAnnouncementsClick?: () => void;
  onAdministrationClick?: () => void;
  onOrdersClick?: () => void;
  onOrderStatisticsClick?: () => void;
  onOrderReportClick?: () => void;
  onSettingsClick?: () => void;
  onLogoutClick?: () => void;
}

const AccountDropdown: React.FC<AccountDropdownProps> = ({
  activeItem,
  hasUnreadMessages = false,
  isAdmin = false,
  onProfileClick,
  onFavoritesClick,
  onLocationsClick,
  onMessagesClick,
  onAnnouncementsClick,
  onAdministrationClick,
  onOrdersClick,
  onOrderStatisticsClick,
  onOrderReportClick,
  onSettingsClick,
  onLogoutClick,
}) => {
  const t = useTranslations();
  const optionProps = (item: AccountDropdownItem) => ({
    className: `dropdown-option ${activeItem === item ? 'active' : ''}`,
    'aria-current': activeItem === item ? 'page' as const : undefined,
  });

  return (
    <div className="account-dropdown">
      <div className="account-dropdown-section">
        {!isAdmin && (
          <>
          <button {...optionProps('profile')} onClick={onProfileClick}>
            <div className="option-icon">
              <UserRound aria-hidden="true" />
            </div>
            <span className="option-text">{t.accountDropdown.profile}</span>
          </button>

          <button {...optionProps('announcements')} onClick={onAnnouncementsClick}>
            <div className="option-icon">
              <StorefrontIcon aria-hidden="true" />
            </div>
            <span className="option-text">{t.accountDropdown.announcements}</span>
          </button>

          <button {...optionProps('favorites')} onClick={onFavoritesClick}>
            <div className="option-icon">
              <Heart aria-hidden="true" />
            </div>
            <span className="option-text">{t.accountDropdown.favorites}</span>
          </button>

          <button {...optionProps('messages')} onClick={onMessagesClick}>
            <div className="option-icon">
              <MessageCircle aria-hidden="true" />
            </div>
            <div className="option-text-container">
              <span className="option-text">{t.accountDropdown.messages}</span>
              {hasUnreadMessages && <div className="dropdown-notification-dot" />}
            </div>
          </button>

          <button {...optionProps('orders')} onClick={onOrdersClick}>
            <div className="option-icon">
              <ClipboardPlus aria-hidden="true" />
            </div>
            <span className="option-text">{t.accountDropdown.orders}</span>
          </button>

          <button {...optionProps('orderStatistics')} onClick={onOrderStatisticsClick}>
            <div className="option-icon">
              <TrendingUp aria-hidden="true" />
            </div>
            <span className="option-text">{t.accountDropdown.orderStatistics}</span>
          </button>

          <button {...optionProps('orderReport')} onClick={onOrderReportClick}>
            <div className="option-icon">
              <ChartNoAxesCombined aria-hidden="true" />
            </div>
            <span className="option-text">{t.accountDropdown.orderReport}</span>
          </button>

          <button {...optionProps('locations')} onClick={onLocationsClick}>
            <div className="option-icon">
              <MapPin aria-hidden="true" />
            </div>
            <span className="option-text">{t.accountDropdown.locations}</span>
          </button>
          </>
        )}

        {isAdmin && (
          <button {...optionProps('administration')} onClick={onAdministrationClick}>
            <div className="option-icon">
              <LayoutDashboard aria-hidden="true" />
            </div>
            <span className="option-text">{t.accountDropdown.administration}</span>
          </button>
        )}
      </div>

      {!isAdmin && (
        <div className="account-dropdown-section account-dropdown-utility-section">
          <button {...optionProps('settings')} onClick={onSettingsClick}>
            <div className="option-icon">
              <Settings aria-hidden="true" />
            </div>
            <span className="option-text">{t.accountDropdown.settings}</span>
          </button>
        </div>
      )}

      <div className="account-dropdown-section account-dropdown-logout-section">
        <button className="dropdown-option logout-option" onClick={onLogoutClick}>
          <div className="option-icon">
            <LogOut aria-hidden="true" />
          </div>
          <span className="option-text">{t.accountDropdown.logout}</span>
        </button>
      </div>
    </div>
  );
};

export default AccountDropdown;
